import { useState, useRef, useEffect } from "react";
import {
  FiUser,
  FiRefreshCw,
  FiSettings,
  FiLogOut,
  FiClock,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { DEFAULT_AVATAR, imageOnError } from "utils/imageFallbacks";

function getInitials(profile) {
  const name = `${profile?.firstname || ""} ${profile?.lastname || ""}`.trim();
  if (!name) return "?";
  const words = name.split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0][0].toUpperCase();
}

export default function ProfileDropdown() {
  const [open, setOpen] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const {
    user, activeProfile, profiles, setActiveProfile, logout,
    subscriptionDaysLeft, isSubscriptionActive,
  } = useAuth();

  const displayName =
    activeProfile
      ? `${activeProfile.firstname || ""}${activeProfile.lastname ? " " + activeProfile.lastname : ""}`.trim()
      : user
      ? `${user.firstname || ""}${user.lastname ? " " + user.lastname : ""}`.trim()
      : "User";

  const email = user?.email || "";
  const profilePicture = activeProfile?.profile_picture || user?.photo || null;

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  // Subscription badge styling
  let subBadgeClass = "border-success bg-success_fade text-success_900";
  let subLabel = "Subscription Active";
  if (isSubscriptionActive === false || (subscriptionDaysLeft !== null && subscriptionDaysLeft <= 0)) {
    subBadgeClass = "border-error bg-error/10 text-error";
    subLabel = "Subscription Expired";
  } else if (subscriptionDaysLeft !== null && subscriptionDaysLeft <= 7) {
    subBadgeClass = "border-yellow-500 bg-yellow-50 text-yellow-700";
    subLabel = `${subscriptionDaysLeft} Day${subscriptionDaysLeft === 1 ? "" : "s"} Until Renewal`;
  } else if (subscriptionDaysLeft !== null) {
    subLabel = `${subscriptionDaysLeft} Days Until Renewal`;
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Mobile: avatar only */}
        <div
          className="flex md:hidden items-center cursor-pointer"
          onClick={() => setOpen((prev) => !prev)}
        >
          {profilePicture ? (
            <img
              src={profilePicture}
              onError={imageOnError(DEFAULT_AVATAR)}
              alt="user"
              className="w-9 h-9 rounded-full border border-neutral_stroke_1 object-cover"
            />
          ) : (
            <div className="w-9 h-9 rounded-full border border-neutral_stroke_1 bg-brand_primary flex items-center justify-center">
              <span className="text-brand_secondary font-aileron_sb text-14">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-4">
          {/* Bell */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 9.00526V9.70979C18.75 10.5554 18.99 11.382 19.4419 12.0856L20.5499 13.8099C21.5609 15.385 20.7889 17.5257 19.03 18.023C14.4337 19.3257 9.5663 19.3257 4.97005 18.023C3.21106 17.5257 2.43907 15.385 3.45006 13.8099L4.55805 12.0856C5.01126 11.3762 5.25176 10.5517 5.25105 9.70979V9.00526C5.25105 5.13635 8.27303 2 12 2C15.727 2 18.75 5.13635 18.75 9.00526Z" fill="black" />
            <circle cx="16" cy="7" r="3" fill="#FF0A0A" />
            <mask id="path-3-inside-1_5408_12486" fill="white">
              <path d="M7 18C7.72778 20.3307 9.69111 22 12 22C14.3089 22 16.2722 20.3307 17 18" />
            </mask>
            <path d="M7.95454 17.7019C7.78993 17.1748 7.22911 16.8808 6.70193 17.0455C6.17475 17.2101 5.88084 17.7709 6.04546 18.2981L7 18L7.95454 17.7019ZM17.9545 18.2981C18.1192 17.7709 17.8252 17.2101 17.2981 17.0455C16.7709 16.8808 16.2101 17.1748 16.0455 17.7019L17 18L17.9545 18.2981ZM7 18L6.04546 18.2981C6.87852 20.9659 9.17489 23 12 23V22V21C10.2073 21 8.57703 19.6954 7.95454 17.7019L7 18ZM12 22V23C14.8251 23 17.1215 20.9659 17.9545 18.2981L17 18L16.0455 17.7019C15.423 19.6954 13.7927 21 12 21V22Z" fill="black" mask="url(#path-3-inside-1_5408_12486)" />
          </svg>

          {/* Clickable group: name + avatar + chevron */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setOpen((prev) => !prev)}
          >
            <div>
              <p className="text-14 font-aileron_sb text-brand_secondary">{displayName}</p>
              <p className="text-14 font-aileron_r text-[#6D6D6D] leading-3">{email}</p>
            </div>

            {profilePicture ? (
              <img
                src={profilePicture}
                onError={imageOnError(DEFAULT_AVATAR)}
                alt="user"
                className="w-10 h-10 rounded-full border border-neutral_stroke_1 object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full border border-neutral_stroke_1 bg-brand_primary flex items-center justify-center">
                <span className="text-brand_secondary font-aileron_sb text-14">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.793 7.73271C16.0787 8.03263 16.0672 8.50737 15.7672 8.79306L10.5168 13.7944C10.2271 14.0703 9.77187 14.0703 9.4822 13.7944L4.23173 8.79306C3.93181 8.50737 3.92028 8.03263 4.20597 7.73271C4.49166 7.43279 4.96639 7.42125 5.26631 7.70694L9.99949 12.2155L14.7327 7.70694C15.0326 7.42125 15.5073 7.43279 15.793 7.73271Z" fill="#242424" />
            </svg>
          </div>
        </div>

        {/* Dropdown menu */}
        {open && (
          <div className="absolute right-0 mt-3 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-lg border border-neutral_stroke_2 p-4 z-50">
            <h3 className="font-albra_sans_sb text-16 mb-3">Hello, {displayName}!</h3>

            <ul className="space-y-2 text-brand_secondary">
              <li
                className="flex items-center gap-3 py-2 hover:bg-neutral_disabled rounded-lg px-3 cursor-pointer"
                onClick={() => { navigate("/settings/account"); setOpen(false); }}
              >
                <FiUser className="text-18" />
                <span className="font-aileron_r text-14">My Account</span>
              </li>

              <li
                className="flex items-center gap-3 py-2 hover:bg-neutral_disabled rounded-lg px-3 cursor-pointer"
                onClick={() => { setShowSwitcher(true); setOpen(false); }}
              >
                <FiRefreshCw className="text-18" />
                <span className="font-aileron_r text-14">Switch User</span>
              </li>

              <li
                className="flex items-center gap-3 py-2 hover:bg-neutral_disabled rounded-lg px-3 cursor-pointer"
                onClick={() => { navigate("/settings/security"); setOpen(false); }}
              >
                <FiSettings className="text-18" />
                <span className="font-aileron_r text-14">Security Settings</span>
              </li>

              <li
                className="flex items-center gap-3 py-2 hover:bg-neutral_disabled rounded-lg px-3 cursor-pointer"
                onClick={handleLogout}
              >
                <FiLogOut className="text-18" />
                <span className="font-aileron_r text-14">Log Out</span>
              </li>
            </ul>

            {/* Subscription badge */}
            <div className={`mt-4 border ${subBadgeClass} rounded-lg py-2 px-4 flex items-center justify-center gap-2 font-aileron_sb text-14`}>
              <FiClock />
              {subLabel}
            </div>
          </div>
        )}
      </div>

      {/* Netflix-style profile switcher overlay */}
      {showSwitcher && (
        <div
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex flex-col items-center justify-center px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSwitcher(false); }}
        >
          <h2 className="text-white text-28 font-albra_sans_b mb-10">Who's learning?</h2>

          {profiles.length === 0 ? (
            <p className="text-white/60 text-14 font-aileron_r">No profiles found.</p>
          ) : (
            <div className="flex flex-wrap justify-center gap-8 max-w-[640px]">
              {profiles.map((profile) => {
                const isActive = activeProfile?.id === profile.id;
                const name = `${profile.firstname || ""}${profile.lastname ? " " + profile.lastname : ""}`.trim() || "Profile";
                const pic = profile.profile_picture || null;
                return (
                  <div
                    key={profile.id}
                    className="flex flex-col items-center gap-3 cursor-pointer group"
                    onClick={() => { setActiveProfile(profile); setShowSwitcher(false); }}
                  >
                    <div className={`w-[100px] h-[100px] rounded-[8px] overflow-hidden border-[3px] transition-all ${isActive ? "border-brand_primary" : "border-transparent group-hover:border-white/70"}`}>
                      {pic ? (
                        <img
                          src={pic}
                          onError={imageOnError(DEFAULT_AVATAR)}
                          alt={name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-brand_primary/40 flex items-center justify-center">
                          <span className="text-white font-aileron_sb text-[36px]">
                            {getInitials(profile)}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className={`text-14 font-aileron_sb transition-colors ${isActive ? "text-brand_primary" : "text-white/70 group-hover:text-white"}`}>
                      {name}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <button
            className="mt-12 text-white/50 hover:text-white text-14 font-aileron_r border border-white/20 hover:border-white/50 px-8 py-2 rounded-full transition"
            onClick={() => setShowSwitcher(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}
